import React, { useEffect, useState, useContext, useMemo } from 'react'
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import { useDispatch, useSelector } from 'react-redux'
import { setSnackBar } from 'state/ui'
import { trackEvent } from 'utils/tracking'
import { usePagination } from 'utils/custom-hooks'
import { Grid } from '@mui/material'
import ArchiveIcon from '@mui/icons-material/Archive'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import { useGetCustomersQuery, useArchiveCustomerMutation } from 'services/customers'
import { SORTABLE_CUSTOMER_FIELDS, CHURN_ZERO_EVENTS as cz } from 'utils/constants'
import CustomerGrid from './CustomerGrid'

const Customers = () => {
  const [isArchiveModalUp, setIsArchiveModalUp] = useState(false)
  const [selectedCustomers, setSelectedCustomers] = useState([])
  const dispatch = useDispatch()
  const { paginateCustomers } = useSelector(state => state.featureFlags?.variables || {})

  const {
    paginationProps,
    listData: customersData,
    isLoading: isLoadingCustomerList,
  } = usePagination({
    canPaginate: paginateCustomers,
    sortFields: SORTABLE_CUSTOMER_FIELDS.DISPLAY_NAME,
    useGetQuery: useGetCustomersQuery,
  })
  const [archiveCustomer, { isLoading: isLoadingArchiveCustomer }] = useArchiveCustomerMutation()

  const isLoading = isLoadingCustomerList || isLoadingArchiveCustomer

  const context = useContext(ContentLayoutContext)

  useEffect(() => {
    context.setContainerTitle('Customers')
  }, [])

  const handleError = error =>
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage:
          error?.customersData?.error ||
          `There was an error archiving the customer${selectedCustomers.length > 1 ? 's' : ''}`,
      }),
    )

  const transformedRows = useMemo(
    () =>
      (customersData?.Items || []).slice().map(customer => ({
        id: customer.id,
        miniId: customer.externalId,
        accountName: customer.displayName,
        customerType:
          (customer.isSupplier && customer.isConsumer && 'Both') ||
          (customer.isSupplier && 'Supplier') ||
          (customer.isConsumer && 'Consumer') ||
          (customer.isCarrier && 'Carrier') ||
          'Neither',
        supplierType: customer.supplierType,
        vendorClass: customer.vendorClass,
        priceList: customer?.priceSheet?.displayName || '',
      })),
    [customersData],
  )

  const actionButtons = [
    {
      text: 'Archive',
      variant: 'outlined',
      icon: <ArchiveIcon />,
      id: 'archive-customers-button',
      onClick: () => setIsArchiveModalUp(true),
    },
  ]

  const archiveSelectedCustomers = () => {
    setIsArchiveModalUp(false)
    selectedCustomers.forEach(async customerId => {
      await archiveCustomer({ customerId })
        .unwrap()
        .then(() => {
          trackEvent(cz.ARCHIVE_CUSTOMER)
        })
        .catch(error => {
          const errorMsg = error?.customersData?.error || 'An error occurred archiving the customer'
          handleError(errorMsg)
          trackEvent({
            eventName: cz.ARCHIVE_CUSTOMER,
            errorMsg,
          })
        })
    })
  }

  return (
    <>
      {isArchiveModalUp && (
        <AlertModal
          open={isArchiveModalUp}
          modalType="void"
          title="Archive Customer"
          question="Are you sure you want to archive these customers?"
          description="Archiving customers is irreversible and you will need to contact GreenSpark support to undo this action."
          confirmLabel="YES, ARCHIVE CUSTOMERS"
          cancelLabel="GO BACK"
          onClose={() => setIsArchiveModalUp(false)}
          onSubmit={archiveSelectedCustomers}
        />
      )}

      <Grid p={3} sx={{ height: '100%' }} container flexDirection="column">
        <CustomerGrid
          rows={transformedRows}
          loading={isLoading}
          centerContent={selectedCustomers.length ? `${selectedCustomers.length} selected` : ''}
          actionButtons={selectedCustomers.length ? actionButtons : []}
          onSelectionModelChange={selectionModel => {
            setSelectedCustomers(selectionModel)
          }}
          keepNonExistentRowsSelected
          autoPageSize={false}
          {...paginationProps}
        />
      </Grid>
    </>
  )
}

export default Customers
